import { useState } from 'react';
import { IconPlus, IconTrash } from '../components/icons';
import { Field, Modal, Skeleton, useAsync, useToast } from '../components/ui';
import { api, Role, Team } from '../lib/api';
import { ago } from '../lib/format';
import { useSession } from '../lib/session';
import { t } from '../lib/i18n';

export const ROLES: { id: Role; label: string; hint: string }[] = [
  { id: 'owner', label: 'Владелец', hint: 'Всё, включая назначение владельцев' },
  { id: 'admin', label: 'Администратор', hint: 'Команда, домены, настройки' },
  { id: 'developer', label: 'Разработчик', hint: 'Ключи, домены, вебхуки, шаблоны' },
  { id: 'viewer', label: 'Наблюдатель', hint: 'Только чтение' },
];
const label = (r: Role) => t(ROLES.find((x) => x.id === r)?.label ?? r);

export function TeamPage() {
  const { me } = useSession();
  const toast = useToast();
  const team = useAsync(() => api.get<Team>('/team'));
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('developer');
  const [error, setError] = useState('');
  const isOwner = me?.user.role === 'owner';
  const manage = isOwner || me?.user.role === 'admin';

  async function invite() {
    setError('');
    try {
      await api.post('/team/invites', { email, role });
      toast(t('Приглашение отправлено на {email}', { email }));
      setOpen(false);
      setEmail('');
      team.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function act(fn: () => Promise<unknown>, ok: string) {
    try {
      await fn();
      toast(ok);
      team.reload();
    } catch (e) {
      toast((e as Error).message, 'bad');
    }
  }

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Команда')}</h1>
          <p>{t('Кто работает в «{name}». Каждый входит своим аккаунтом — так журнал действий остаётся честным.', { name: me?.workspace.name ?? '' })}</p>
        </div>
        {manage && <button className="btn btn-primary" onClick={() => setOpen(true)}><IconPlus size={16} /> {t('Пригласить')}</button>}
      </div>

      <div className="card">
        <div className="card-head"><h3>{t('Участники')}</h3><span className="small muted">{team.data?.members.length ?? '…'}</span></div>
        {team.loading && !team.data ? (
          <div className="card-pad stack"><Skeleton /><Skeleton /></div>
        ) : (
          <table className="table">
            <thead><tr><th>{t('Имя')}</th><th>Email</th><th>{t('Роль')}</th><th>2FA</th><th>{t('С нами')}</th><th /></tr></thead>
            <tbody>
              {team.data?.members.map((m) => {
                const self = m.user_id === me?.user.id;
                const canEdit = manage && !self && (isOwner || m.role !== 'owner');
                return (
                  <tr key={m.user_id}>
                    <td><strong>{m.name}</strong>{self && <span className="small muted"> {t('· это вы')}</span>}</td>
                    <td className="muted">{m.email}</td>
                    <td>
                      {canEdit ? (
                        <select
                          className="select input"
                          style={{ width: 170, height: 32 }}
                          value={m.role}
                          onChange={(e) => act(() => api.patch(`/team/members/${m.user_id}`, { role: e.target.value }), t('Роль изменена'))}
                        >
                          {ROLES.filter((r) => isOwner || r.id !== 'owner').map((r) => <option key={r.id} value={r.id}>{t(r.label)}</option>)}
                        </select>
                      ) : (
                        <span className={`pill ${m.role === 'owner' ? 'sent' : m.role === 'viewer' ? 'suppressed' : 'queued'}`}>{label(m.role)}</span>
                      )}
                    </td>
                    <td>{m.totp_enabled ? <span className="pill ok">{t('вкл')}</span> : <span className={`pill ${me?.workspace.require_2fa ? 'bad' : 'suppressed'}`}>{t('нет')}</span>}</td>
                    <td className="small muted">{ago(m.joined_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {canEdit && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => confirm(t('Удалить {name} из пространства? Сессии будут завершены.', { name: m.name })) && act(() => api.del(`/team/members/${m.user_id}`), t('Участник удалён'))}
                        >
                          <IconTrash size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!!team.data?.invites.length && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-head"><h3>{t('Ждут ответа')}</h3></div>
          <table className="table">
            <tbody>
              {team.data.invites.map((i) => (
                <tr key={i.id}>
                  <td><strong>{i.email}</strong></td>
                  <td><span className="pill pending">{label(i.role)}</span></td>
                  <td className="small muted">{t('отправлено {sent} · истекает {exp}', { sent: ago(i.created_at), exp: ago(i.expires_at) })}</td>
                  <td style={{ textAlign: 'right' }}>
                    {manage && <button className="btn btn-sm btn-danger" onClick={() => act(() => api.del(`/team/invites/${i.id}`), t('Приглашение отозвано'))}>{t('Отозвать')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Modal
          title={t('Пригласить в команду')}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button className="btn" onClick={() => setOpen(false)}>{t('Отмена')}</button>
              <button className="btn btn-primary" onClick={invite} disabled={!email}>{t('Отправить приглашение')}</button>
            </>
          }
        >
          <Field label="Email" error={error}>
            <input className="input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@company.com" />
          </Field>
          <Field label={t('Роль')}>
            <div className="choice-grid">
              {ROLES.filter((r) => r.id !== 'owner' && (isOwner || r.id !== 'admin')).map((r) => (
                <button key={r.id} type="button" className={`choice${role === r.id ? ' on' : ''}`} onClick={() => setRole(r.id)}>
                  <strong>{t(r.label)}</strong>
                  <span>{t(r.hint)}</span>
                </button>
              ))}
            </div>
          </Field>
          <p className="small muted">{t('Ссылка действует 7 дней. Владельцами становятся только через смену роли владельцем.')}</p>
        </Modal>
      )}
    </>
  );
}
